import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import FlightIcon from "@mui/icons-material/Flight";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

import type { AssignedLimitationInput, LimitationDefinition } from "src/shared/api-types/limitations";

import ExpandableSection from "../../../../components/ExpandableSection";
import Confirm from "../../../../components/Confirm";
import type { AircraftSection as AircraftSectionType, LinkedOperation } from "../types";
import OperationsList from "./OperationsList";

interface AircraftSectionProps {
  section: AircraftSectionType;
  userId: number;
  crewId: number;
  isFiltered: boolean;
  canEdit: boolean;
  limitationDefinitions: LimitationDefinition[];
  onToggle: (
    typeId: number,
    operationId: number,
    enabled: boolean,
    underSupervision: boolean,
    limitations?: AssignedLimitationInput[],
  ) => Promise<unknown>;
  onCopy: (sourceOperation: LinkedOperation, allOperations: LinkedOperation[]) => void;
  onUnassign?: (typeId: number) => void;
  expanded?: boolean;
  onExpandChange?: (value: boolean) => void;
}

const AircraftSectionTitle: React.FC<{
  name: string;
  designatorName?: string;
  onUnassignClick?: () => void;
}> = ({ name, designatorName, onUnassignClick }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FlightIcon fontSize="small" />
        <Typography variant="body1">{name}</Typography>
        {designatorName && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            ({designatorName})
          </Typography>
        )}
      </Box>
      {onUnassignClick && (
        <IconButton
          size="small"
          title={t("aircraft_management.unassign_aircraft_type")}
          onClick={(e) => {
            e.stopPropagation();
            (e.currentTarget as HTMLElement).blur();
            onUnassignClick();
          }}
        >
          <DeleteForeverIcon color="error" fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const AircraftSection: React.FC<AircraftSectionProps> = ({
  section,
  userId,
  crewId,
  isFiltered,
  canEdit,
  limitationDefinitions,
  onToggle,
  onCopy,
  onUnassign,
  expanded,
  onExpandChange,
}) => {
  const { t } = useTranslation();
  const { aircraftType, operations } = section;
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <ExpandableSection
        navId={`aircraft-section-${aircraftType.typeId}`}
        expanded={expanded}
        onChange={onExpandChange}
        title={
          <AircraftSectionTitle
            name={aircraftType.typeName}
            designatorName={aircraftType.designatorName}
            onUnassignClick={onUnassign ? () => setConfirmOpen(true) : undefined}
          />
        }
      >
        <OperationsList
          operations={operations}
          userId={userId}
          crewId={crewId}
          aircraftTypeName={aircraftType.typeName}
          isFiltered={isFiltered}
          canEdit={canEdit}
          limitationDefinitions={limitationDefinitions}
          onToggle={onToggle}
          onCopy={onCopy}
        />
      </ExpandableSection>

      <Confirm
        open={confirmOpen}
        title={t("aircraft_management.unassign_confirm_title")}
        confirmColor="error"
        onConfirm={(result) => {
          setConfirmOpen(false);
          if (result) onUnassign?.(aircraftType.typeId);
        }}
      >
        {t("aircraft_management.unassign_confirm_message", { aircraft_type: aircraftType.typeName })}
      </Confirm>
    </>
  );
};

export default AircraftSection;
